import React, { useEffect, useMemo, useState } from 'react';
import { Steps, UseStepper } from 'headless-stepper';
import { FaCheck } from 'react-icons/fa';
import { ImCross } from 'react-icons/im';

// Styles
import styles from './stepper.module.scss';

interface StepperOptions {
	canJumpToStepsAhead?: boolean;
	hideStepsNumbers?: boolean;
	hideLabels?: boolean;
	hideDisabledSteps?: boolean;
}

interface StepperProps {
	steps: Steps[];
	stepper: UseStepper;
	options?: StepperOptions;
}

export const Stepper = ({ steps = [], stepper, options }: StepperProps) => {
	// State
	const [numberOfStepsToShow, setNumberOfStepsToShow] = useState(stepper.state?.totalSteps);
	const { canJumpToStepsAhead, hideStepsNumbers, hideLabels, hideDisabledSteps } = options ?? {};

	// Memo
	const stepsContainerMaxWidth = useMemo(() => {
		const initialMaxWidth = 990;
		const maxWidthBetweenSteps = 120;
		const maxWidthBySteps = maxWidthBetweenSteps * (numberOfStepsToShow - 1) + 38; // add $step-circle-diameter = 38px
		const minMaxWidth = Math.min(initialMaxWidth, maxWidthBySteps);
		return `${minMaxWidth}px`;
	}, [numberOfStepsToShow]);

	// Life cycle
	useEffect(() => {
		const numberOfDisabledSteps = steps.filter(step => step.disabled).length;
		const numberOfTotalSteps = steps.length;
		const nextNumberOfStepsToShow = hideDisabledSteps
			? numberOfTotalSteps - numberOfDisabledSteps
			: numberOfTotalSteps;
		setNumberOfStepsToShow(nextNumberOfStepsToShow);
	}, [steps, hideDisabledSteps]);

	// Debug steps
	/* useEffect(() => {
		console.group('%c Stepper steps', 'color: hotpink');
		for (let i = 0; i < steps.length; i++) {
			console.log(`steps[${i}]`, steps[i]);
		}
		console.groupEnd();
	}, [steps]); */

	// Helpers
	const isStepPending = (stepIndex: number) => {
		return steps[stepIndex].isValid == undefined;
	};

	const isStepCurrent = (stepIndex: number) => {
		return stepper.state?.currentStep === stepIndex;
	};

	const isStepSuccess = (stepIndex: number) => {
		return !isStepPending(stepIndex) && steps[stepIndex].isValid;
	};

	const isStepError = (stepIndex: number) => {
		return !isStepPending(stepIndex) && !steps[stepIndex].isValid;
	};

	const isStepDisabled = (stepIndex: number) => {
		return steps[stepIndex].disabled;
	};

	const relativeStepIndex = (stepIndex: number) => {
		if (!hideDisabledSteps) {
			return stepIndex;
		}
		let relativeStep = stepIndex;
		for (let i = 0; i < stepIndex; i++) {
			if (isStepDisabled(i)) {
				relativeStep--;
			}
		}
		return relativeStep;
	};

	const calculateProgressWidth = () => {
		let lastCheckpointStep = 0;
		let stepIndex = 1;
		while (
			stepIndex < steps.length &&
			(isStepSuccess(stepIndex - 1) || isStepDisabled(stepIndex - 1))
		) {
			lastCheckpointStep = stepIndex;
			stepIndex++;
		}
		const progressWidth = `${
			(relativeStepIndex(lastCheckpointStep) / (numberOfStepsToShow - 1)) * 100
		}%`;
		return progressWidth;
	};

	return (
		<div className={styles.stepper}>
			<nav
				className={styles.steps}
				style={{ maxWidth: stepsContainerMaxWidth }}
				{...stepper.stepperProps}
			>
				<div className={styles.line}></div>
				<div
					className={styles.lineProgress}
					style={{
						width: calculateProgressWidth(),
					}}
				></div>
				{stepper.stepsProps?.map((step, index) => {
					const key = `step-${index}`;
					if (isStepCurrent(index)) {
						return (
							<a key={key} {...step} className={[styles.step, styles['step-active']].join(' ')}>
								<span></span>
								{!hideLabels && <span className={styles.label}>{steps[index].label}</span>}
							</a>
						);
					} else if (isStepSuccess(index)) {
						return (
							<a key={key} {...step} className={[styles.step, styles['step-success']].join(' ')}>
								<FaCheck className={styles['step-icon-success']} />
								{!hideLabels && <span className={styles.label}>{steps[index].label}</span>}
							</a>
						);
					} else if (isStepError(index)) {
						return (
							<a key={key} {...step} className={[styles.step, styles['step-error']].join(' ')}>
								<ImCross className={styles['step-icon-error']} />
								{!hideLabels && <span className={styles.label}>{steps[index].label}</span>}
							</a>
						);
					} else if (isStepDisabled(index)) {
						if (!hideDisabledSteps) {
							return (
								<a key={key} {...step} className={[styles.step, styles['step-disabled']].join(' ')}>
									<span></span>
									{!hideLabels && (
										<span className={[styles.label, styles.labelDisabled].join(' ')}>
											{steps[index].label}
										</span>
									)}
								</a>
							);
						} else {
							return null;
						}
					} else {
						// Pending step
						return (
							<a
								key={key}
								{...(canJumpToStepsAhead ? step : {})}
								className={[styles.step, !canJumpToStepsAhead && styles['step-non-clickable']].join(
									' ',
								)}
							>
								{!hideStepsNumbers && <span>{relativeStepIndex(index) + 1}</span>}
								{!hideLabels && <span className={styles.label}>{steps[index].label}</span>}
							</a>
						);
					}
				})}
			</nav>
		</div>
	);
};
